import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import SubmitCase from "@/pages/SubmitCase";
import CaseDetails from "@/pages/CaseDetails";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 180000, // 3 minutes timeout for AI analysis
});

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAuth, setProcessingAuth] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      console.log('Current hash:', hash);
      console.log('Current pathname:', window.location.pathname);
      
      if (hash && hash.includes("session_id=")) {
        setProcessingAuth(true);
        const sessionId = hash.split("session_id=")[1].split("&")[0];
        console.log('Processing session_id:', sessionId);
        
        try {
          console.log('Calling /auth/session endpoint...');
          console.log('Current origin:', window.location.origin);
          console.log('Backend URL:', BACKEND_URL);
          const sessionResponse = await api.post("/auth/session", { session_id: sessionId });
          console.log('Session response:', sessionResponse.data);
          
          // Store token in localStorage as fallback for cross-domain cookie issues
          if (sessionResponse.data.session_token) {
            console.log('Storing session token in localStorage');
            localStorage.setItem('session_token', sessionResponse.data.session_token);
            // Set Authorization header for all future requests
            api.defaults.headers.common['Authorization'] = `Bearer ${sessionResponse.data.session_token}`;
          }
          
          // Clear the hash from URL
          window.history.replaceState(null, "", window.location.pathname);
          
          // Small delay to ensure cookie is set
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Fetch user data
          console.log('Fetching user data...');
          const response = await api.get("/auth/me");
          console.log('User data received:', response.data);
          setUser(response.data);
          setProcessingAuth(false);
          setLoading(false);
          
          // Navigate to dashboard
          console.log('Navigating to dashboard...');
          navigate("/dashboard", { replace: true });
          return;
        } catch (error) {
          console.error("Auth error:", error);
          console.error("Error response:", error.response?.data);
          console.error("Error status:", error.response?.status);
          setProcessingAuth(false);
          setLoading(false);
        }
      }

      // Check existing session
      try {
        // Check localStorage for token first
        const storedToken = localStorage.getItem('session_token');
        if (storedToken) {
          console.log('Found token in localStorage, setting Authorization header');
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        }
        
        console.log('Checking existing session...');
        const response = await api.get("/auth/me");
        console.log('Existing session found:', response.data);
        setUser(response.data);
      } catch (error) {
        console.log('No existing session found');
        // Clear localStorage if auth fails
        localStorage.removeItem('session_token');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname, navigate]);

  if (loading || processingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">{processingAuth ? "Authenticating..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
        <Route path="/dashboard" element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/" />} />
        <Route path="/submit" element={user ? <SubmitCase user={user} /> : <Navigate to="/" />} />
        <Route path="/case/:caseId" element={user ? <CaseDetails user={user} /> : <Navigate to="/" />} />
      </Routes>
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;